/**
 * 인터뷰 준비 케이스 — 운영자 화면 `/admin/geo-cases` (261023-geo-watch-agent).
 *
 * 개선 에이전트가 「인용된 우리 랭킹 페이지에서 고객 순위가 낮아 브랜드가 답변에 빠진다」를 발견하면 올린다.
 * 운영자는 이 화면 하나로 고객과 인터뷰하고, 같은 화면의 프로필 카드(`updateBrand`)로 곧바로 고친다.
 * ★ 운영자 전용(`admin`) — 경쟁 업체의 점수표와 분석이 실린다. 고객 화면에 싣지 않는다.
 */
import { z } from 'zod';
import { CaseQuestion, CaseRankingTable } from '../agent.ts';
import { IdParams, IsoDateTime, Ok } from '../common.ts';
import { defineRoute } from '../route.ts';

export const CaseStatus = z.enum(['open', 'applied', 'dismissed']);

export const GeoCaseRow = z.object({
  id: z.uuid(),
  brandId: z.uuid(),
  brandName: z.string(),
  queryId: z.uuid(),
  query: z.string(),
  pageUrl: z.string(),
  status: CaseStatus,
  /** 그 페이지에서 고객의 순위와 후보 수 — 산정표가 없으면 null */
  rank: z.number().int().nullable(),
  candidates: z.number().int().nullable(),
  /** 케이스 뒤에 브랜드 프로필이 바뀌었는가 — 다음 에이전트 실행이 반영한다 */
  profileUpdatedAfter: z.boolean(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type GeoCaseRow = z.infer<typeof GeoCaseRow>;

export const GeoCaseDetail = GeoCaseRow.extend({
  analysisMd: z.string(),
  /** 화면이 기존 `sample` 계약으로 원문을 읽는다 */
  sampleIds: z.array(z.uuid()),
  rankingTable: CaseRankingTable.nullable(),
  questions: z.array(CaseQuestion),
  resolvedNote: z.string().nullable(),
  resolvedAt: IsoDateTime.nullable(),
});
export type GeoCaseDetail = z.infer<typeof GeoCaseDetail>;

export const listGeoCases = defineRoute({
  method: 'get',
  path: '/api/geo-cases',
  access: 'admin',
  summary: '인터뷰 준비 케이스 목록 — 열린 것이 먼저',
  query: z.object({ status: CaseStatus.optional() }),
  response: z.object({ cases: z.array(GeoCaseRow) }),
});

export const getGeoCase = defineRoute({
  method: 'get',
  path: '/api/geo-cases/{id}',
  access: 'admin',
  summary: '인터뷰 준비 케이스 하나',
  params: IdParams,
  response: z.object({ case: GeoCaseDetail }),
});

export const dismissGeoCase = defineRoute({
  method: 'post',
  path: '/api/geo-cases/{id}/dismiss',
  access: 'admin',
  summary: '케이스를 닫는다(인터뷰가 필요 없거나 할 수 없을 때)',
  params: IdParams,
  body: z.object({ note: z.string().trim().min(1).max(2000) }).strict(),
  response: Ok,
});
